import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { Receipt, Loader2, AlertCircle, RotateCcw, Send, ChevronDown, CheckCircle2, ShoppingCart, ListChecks } from 'lucide-react';

import CustomDatePicker from '../../components/CustomDatePicker';

// Vendor Cash Payment slice
import {
    fetchSppoPaymentTypes,
    fetchSppoVendors,
    fetchSppoPOs,
    fetchSppoInvoices,
    saveVendorCashPayment,
    clearVendors,
    clearPOs,
    clearInvoices,
    clearSaveResult,
    resetVendorCash,
    selectPaymentTypes,
    selectVcpVendors,
    selectVcpPOList,
    selectVcpInvoiceList,
    selectVcpSaveStatus,
    selectVcpSaveError,
    selectPaymentTypesLoading,
    selectVcpVendorsLoading,
    selectVcpPOLoading,
    selectVcpInvoiceLoading,
    selectVcpSaveLoading,
    selectVcpVendorsError,
    selectVcpPOError,
    selectVcpInvoiceError,
} from '../../slices/accountsSlice/vendorCashPaymentSlice';

// Reuse CC lists from cashVoucherSlice (fetches the same data)
import {
    fetchOtherCCList,
    resetCashVoucher,
    selectOtherCCList,
    selectOtherCCLoading,
} from '../../slices/accountsSlice/cashVoucherSlice';

import { checkVendorInvPayDates } from '../../api/PurchaseAPI/sppoPaymentAPI';
import { getVendorPaymentCC } from '../../api/businessinfoAPI/vendorPOAPI';
// ─── Shared Helper Components (same as CashVoucherCreation) ───────────────────

const fmt = (val) => {
    const n = parseFloat(val);
    if (isNaN(n)) return '0.00';
    return n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

// Legacy ValidateAmountQuantity: digits with at most 2 decimals
const isAmountInput = (v) => /^\d*\.?\d{0,2}$/.test(v);

const inputCls =
    'w-full h-9 px-2.5 rounded-lg border text-sm bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 border-gray-300 dark:border-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-200 dark:focus:ring-indigo-900/40 focus:border-indigo-500 disabled:bg-gray-100 dark:disabled:bg-gray-700/60 disabled:cursor-not-allowed';
const selectCls = `${inputCls} appearance-none pr-8 cursor-pointer`;

const CardHeader = ({ icon: Icon, title }) => (
    <div className="px-4 py-2.5 border-b border-gray-100 dark:border-gray-700 flex items-center gap-2 bg-gray-50/60 dark:bg-gray-900/40 rounded-t-xl">
        <Icon className="h-4 w-4 text-indigo-500" />
        <h2 className="text-sm font-bold text-gray-800 dark:text-gray-100">{title}</h2>
    </div>
);

// Summary panel row
const SumRow = ({ label, value }) => (
    <div className="flex justify-between gap-3">
        <dt className="text-gray-500 dark:text-gray-400 flex-shrink-0">{label}</dt>
        <dd className="font-semibold text-gray-800 dark:text-gray-100 truncate text-right" title={value || ''}>{value || '—'}</dd>
    </div>
);

const Field = ({ label, required, className = '', children }) => (
    <div className={`min-w-0 ${className}`}>
        <label className="block text-[11px] font-bold text-gray-600 dark:text-gray-400 uppercase tracking-wide mb-1">
            {label}{required && <span className="text-rose-500 ml-0.5">*</span>}
        </label>
        {children}
    </div>
);

const Select = ({ value, onChange, disabled, loading, placeholder = '-Please Select-', options, getValue, getLabel }) => (
    <div className="relative">
        <select value={value} onChange={(e) => onChange(e.target.value)} disabled={disabled || loading} className={selectCls}>
            <option value="">{loading ? 'Loading...' : placeholder}</option>
            {options.map((o, idx) => (
                <option key={getValue(o) || idx} value={getValue(o)}>{getLabel(o)}</option>
            ))}
        </select>
        <div className="pointer-events-none absolute inset-y-0 right-2 flex items-center">
            {loading ? <Loader2 className="h-4 w-4 text-indigo-500 animate-spin" /> : <ChevronDown className="h-4 w-4 text-gray-400" />}
        </div>
    </div>
);

const SubHeading = ({ icon: Icon, children }) => (
    <p className="flex items-center gap-1.5 text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wide mb-2">
        <Icon className="h-3.5 w-3.5 text-indigo-500" /> {children}
    </p>
);

const ErrorLine = ({ children }) => (
    <div className="mb-2 flex items-center gap-1.5 rounded-lg border px-3 py-2 text-xs bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-700 text-red-700 dark:text-red-300">
        <AlertCircle className="h-3.5 w-3.5 flex-shrink-0" /> {children}
    </div>
);

const MODE_SELF  = 'Self Debit';
const MODE_OTHER = 'Debit from Other CC';

// ─── Main Component ────────────────────────────────────────────────────────────
const VendorCashPayment = ({ menuData }) => {
    const dispatch = useDispatch();
    const { userData } = useSelector((s) => s.auth);

    const roleId   = userData?.roleId   || userData?.RID  || 0;
    const userId   = userData?.userId   || userData?.UID  || userData?.employeeId || '';
    const userName = userData?.userName || userData?.UserName || 'User';

    // ── Selectors: vendorCashPaymentSlice ─────────────────────────────────────
    const paymentTypes       = useSelector(selectPaymentTypes);
    const vendors            = useSelector(selectVcpVendors);
    const poList             = useSelector(selectVcpPOList);
    const invoiceList        = useSelector(selectVcpInvoiceList);
    const saveStatus         = useSelector(selectVcpSaveStatus);
    const saveError          = useSelector(selectVcpSaveError);
    const paymentTypesLoading = useSelector(selectPaymentTypesLoading);
    const vendorsLoading     = useSelector(selectVcpVendorsLoading);
    const poLoading          = useSelector(selectVcpPOLoading);
    const invoiceLoading     = useSelector(selectVcpInvoiceLoading);
    const saveLoading        = useSelector(selectVcpSaveLoading);
    const vendorsError       = useSelector(selectVcpVendorsError);
    const poError            = useSelector(selectVcpPOError);
    const invoiceError       = useSelector(selectVcpInvoiceError);

    // ── Selectors: cashVoucherSlice (CC lists) ────────────────────────────────
    const otherCCList   = useSelector(selectOtherCCList);
    const otherCCLoading = useSelector(selectOtherCCLoading);

    // ── Local State ───────────────────────────────────────────────────────────
    // Self CC list: legacy SPPOPayments uses GetVendorPaymentCC().UserCClist (user's open CCs with approved
    // vendor invoices). Mapped to the { CC_Id, CC_Code: "code , name" } shape the dropdown renders.
    const [selfCCList,      setSelfCCList]      = useState([]);
    const [selfCCLoading,   setSelfCCLoading]   = useState(false);
    const [vendorType,      setVendorType]      = useState('');          // 'Supplier' | 'Service Provider'
    const [paymentType,     setPaymentType]     = useState('');          // from dropdown
    const [txnMode,         setTxnMode]         = useState('');          // 'Self Debit' | 'Debit from Other CC'
    const [selfCC,          setSelfCC]          = useState('');          // CC_Code value
    const [otherCC,         setOtherCC]         = useState('');          // CC_Code value
    const [vendorCode,      setVendorCode]      = useState('');
    const [selectedPOs,     setSelectedPOs]     = useState([]);          // array of PO number strings
    const [paymentDate,     setPaymentDate]     = useState(null);
    const [remarks,         setRemarks]         = useState('');
    const [advanceAmount,   setAdvanceAmount]   = useState('');          // used only for Vendor Advance

    // Invoice selections: { [InvoiceNo]: { selected: bool, payAmount: string } }
    const [invoiceSelections, setInvoiceSelections] = useState({});

    // ── Derived Values ────────────────────────────────────────────────────────
    const isAdvance = paymentType === 'Vendor Advance';

    // effectiveCCCode: the CC that gets passed to vendor/PO/invoice APIs
    const effectiveCCCode = txnMode === 'Debit from Other CC' ? otherCC : selfCC;

    // Selected invoice objects with their pay amounts
    const selectedInvoiceData = invoiceList.filter(
        (inv) => invoiceSelections[inv.InvoiceNo]?.selected
    ).map((inv) => ({
        ...inv,
        payAmount: parseFloat(invoiceSelections[inv.InvoiceNo]?.payAmount) || 0,
    }));

    // Total amount: sum of payAmounts for non-advance, or advanceAmount for advance
    const totalAmount = isAdvance
        ? parseFloat(advanceAmount) || 0
        : selectedInvoiceData.reduce((sum, inv) => sum + inv.payAmount, 0);

    // ── On mount: fetch self CC list ──────────────────────────────────────────
    useEffect(() => {
        if (userId && roleId) {
            setSelfCCLoading(true);
            getVendorPaymentCC(roleId, userId)
                .then((res) => setSelfCCList((res?.Data?.UserCClist || []).map((c) => ({ CC_Id: c.CC_Code, CC_Code: c.CC_Name || c.CC_Code }))))
                .catch(() => { setSelfCCList([]); toast.error('Failed to load cost centers'); })
                .finally(() => setSelfCCLoading(false));
        }
        return () => {
            dispatch(resetVendorCash());
            dispatch(resetCashVoucher());
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, userId, roleId]);

    // ── Fetch other CC list when mode switches ────────────────────────────────
    // Legacy SPPOPayments: GetAllCashCC(cc = selected self CC) — the list excludes the self CC.
    useEffect(() => {
        if (txnMode === 'Debit from Other CC' && selfCC) {
            dispatch(fetchOtherCCList({ id: selfCC, type: '' }));
        }
    }, [txnMode, dispatch, selfCC]);

    // ── Fetch payment types when vendor type is selected ─────────────────────
    useEffect(() => {
        if (vendorType) {
            dispatch(fetchSppoPaymentTypes(vendorType));
            setPaymentType('');
            dispatch(clearVendors());
            dispatch(clearPOs());
            dispatch(clearInvoices());
            setVendorCode('');
            setSelectedPOs([]);
            setInvoiceSelections({});
        }
    }, [vendorType, dispatch]);

    // ── Fetch vendors when CC is ready (after vendor type + payment type + CC) ─
    useEffect(() => {
        if (vendorType && paymentType && effectiveCCCode) {
            dispatch(fetchSppoVendors({ vendorType, ccCode: effectiveCCCode, paymentType }));
            setVendorCode('');
            dispatch(clearPOs());
            dispatch(clearInvoices());
            setSelectedPOs([]);
            setInvoiceSelections({});
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [vendorType, paymentType, effectiveCCCode, dispatch]);

    // ── Handle save result ────────────────────────────────────────────────────
    useEffect(() => {
        if (saveStatus === 'success') {
            toast.success('Vendor cash payment saved successfully!');
            dispatch(clearSaveResult());
            handleReset();
        } else if (saveStatus === 'failed' && saveError) {
            toast.error(saveError);
            dispatch(clearSaveResult());
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [saveStatus, saveError, dispatch]);

    // ── Handlers ─────────────────────────────────────────────────────────────

    const handleVendorTypeChange = (val) => {
        setVendorType(val);
        setPaymentType('');
        setTxnMode('');
        setSelfCC('');
        setOtherCC('');
        setVendorCode('');
        setSelectedPOs([]);
        setInvoiceSelections({});
        setPaymentDate(null);
        setRemarks('');
        setAdvanceAmount('');
        dispatch(clearVendors());
        dispatch(clearPOs());
        dispatch(clearInvoices());
    };

    const handlePaymentTypeChange = (val) => {
        setPaymentType(val);
        setVendorCode('');
        setSelectedPOs([]);
        setInvoiceSelections({});
        dispatch(clearVendors());
        dispatch(clearPOs());
        dispatch(clearInvoices());
    };

    const handleTxnModeChange = (val) => {
        setTxnMode(val);
        setSelfCC('');
        setOtherCC('');
        setVendorCode('');
        setSelectedPOs([]);
        setInvoiceSelections({});
        dispatch(clearVendors());
        dispatch(clearPOs());
        dispatch(clearInvoices());
    };

    const handleSelfCCChange = useCallback((val) => {
        setSelfCC(val);
        setVendorCode('');
        setSelectedPOs([]);
        setInvoiceSelections({});
        dispatch(clearPOs());
        dispatch(clearInvoices());
    }, [dispatch]);

    const handleOtherCCChange = useCallback((val) => {
        setOtherCC(val);
        setVendorCode('');
        setSelectedPOs([]);
        setInvoiceSelections({});
        dispatch(clearPOs());
        dispatch(clearInvoices());
    }, [dispatch]);

    const handleVendorChange = useCallback((val) => {
        setVendorCode(val);
        setSelectedPOs([]);
        setInvoiceSelections({});
        dispatch(clearPOs());
        dispatch(clearInvoices());
        if (val && vendorType && effectiveCCCode && paymentType) {
            dispatch(fetchSppoPOs({ vendorCode: val, vendorType, ccCode: effectiveCCCode, paymentType }));
        }
    }, [dispatch, vendorType, effectiveCCCode, paymentType]);

    // Toggle a PO checkbox; fetch invoices when selection changes (non-advance)
    const handlePOToggle = useCallback((poNo) => {
        setSelectedPOs((prev) => {
            const next = prev.includes(poNo)
                ? prev.filter((p) => p !== poNo)
                : [...prev, poNo];

            // Fetch invoices for non-advance types
            if (!isAdvance && next.length > 0 && vendorCode) {
                const poNos = next.join(',') + ',';
                dispatch(fetchSppoInvoices({ vendorCode, poNos, payType: paymentType }));
            } else if (next.length === 0) {
                dispatch(clearInvoices());
                setInvoiceSelections({});
            }
            return next;
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, isAdvance, vendorCode, paymentType]);

    // Reset invoice selections when invoiceList changes (new fetch)
    useEffect(() => {
        setInvoiceSelections({});
    }, [invoiceList]);

    // Toggle an invoice row checkbox
    const handleInvoiceToggle = (invoiceNo, balance) => {
        setInvoiceSelections((prev) => {
            const existing = prev[invoiceNo];
            if (existing?.selected) {
                // Deselect
                return { ...prev, [invoiceNo]: { selected: false, payAmount: existing.payAmount } };
            }
            return {
                ...prev,
                [invoiceNo]: {
                    selected: true,
                    payAmount: existing?.payAmount || String(balance || ''),
                },
            };
        });
    };

    // Update pay amount for a specific invoice (capped at balance)
    const handlePayAmountChange = (invoiceNo, value, balance) => {
        const num = parseFloat(value);
        const cap = parseFloat(balance) || 0;
        const clamped = !isNaN(num) && num > cap ? String(cap) : value;
        setInvoiceSelections((prev) => ({
            ...prev,
            [invoiceNo]: { ...prev[invoiceNo], payAmount: clamped },
        }));
    };

    const handleReset = () => {
        setVendorType('');
        setPaymentType('');
        setTxnMode('');
        setSelfCC('');
        setOtherCC('');
        setVendorCode('');
        setSelectedPOs([]);
        setInvoiceSelections({});
        setPaymentDate(null);
        setRemarks('');
        setAdvanceAmount('');
        dispatch(clearVendors());
        dispatch(clearPOs());
        dispatch(clearInvoices());
    };

    const handleSubmit = async () => {
        // Validation
        if (!vendorType)    { toast.warn('Please select Vendor Type.');     return; }
        if (!paymentType)   { toast.warn('Please select Payment Type.');    return; }
        if (!txnMode)       { toast.warn('Please select Transaction Mode.'); return; }
        if (!selfCC)        { toast.warn('Please select Self Cost Center.'); return; }
        if (txnMode === 'Debit from Other CC' && !otherCC) {
            toast.warn('Please select Other Cost Center.'); return;
        }
        if (!vendorCode)    { toast.warn('Please select a Vendor.');        return; }
        if (!paymentDate)   { toast.warn('Please select Payment Date.');    return; }

        if (!isAdvance) {
            if (selectedPOs.length === 0) {
                toast.warn('Please select at least one PO.'); return;
            }
            if (selectedInvoiceData.length === 0) {
                toast.warn('Please select at least one invoice.'); return;
            }
            const hasAmount = selectedInvoiceData.some((inv) => inv.payAmount > 0);
            if (!hasAmount) {
                toast.warn('Please enter pay amount for selected invoices.'); return;
            }
        } else {
            // Legacy advance takes a single PO (spInsertVendorSPPOAdvnacePayment matches SPPONo = @Ponos exactly).
            if (selectedPOs.length !== 1) {
                toast.warn('Please select one PO for the advance.'); return;
            }
            if (!advanceAmount || parseFloat(advanceAmount) <= 0) {
                toast.warn('Please enter a valid Advance Amount.'); return;
            }
        }

        // Legacy: all selected invoice dates ('d1,d2,') — the SPs and CheckVendorInvPayDates loop over them.
        const allInvoiceDates = !isAdvance
            ? selectedInvoiceData.map((inv) => inv.SPPOInvoiceDate || inv.InvoiceDate || '').join(',') + ','
            : '';

        // Format payment date as DD-Mon-YYYY (e.g. 20-Mar-2026) to match API expectation
        const txnDate = paymentDate
            ? new Date(paymentDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }).replace(/ /g, '-')
            : '';

        // Legacy CheckVendorInvPayDates: 'Yes' = an invoice date is after the payment date → ask before saving.
        if (!isAdvance) {
            const chk = await checkVendorInvPayDates({ MaxInvoiceDate: allInvoiceDates, TransactionDate: txnDate }).catch(() => null);
            const chkStr = String(chk?.Data ?? chk ?? '').trim();
            if (chkStr === 'Yes'
                && !window.confirm('Invoice Date is Greater Than Payment Date. Do You Really Want to Submit the Payment?')) {
                return;
            }
        }

        // Build common payload
        const payload = {
            // Advance: the single PO (no trailing comma). Invoices: one PO per selected invoice — the SP pairs
            // invoice ↔ PO by position (legacy Ponos).
            PONumber:          isAdvance
                ? selectedPOs[0]
                : selectedInvoiceData.map((inv) => inv.SPPONo || inv.PONo || inv.PONumber || '').join(',') + ',',
            VendorCode:        vendorCode,
            TransactionDate:   txnDate,
            BankName:          '',
            ModeofPay:         '',
            BankId:            '',
            TransactionAmount: totalAmount,
            Remarks:           remarks.trim(),
            Number:            '',
            Paytype:           paymentType,
            Createdby:         userName,
            Roleid:            roleId,
            BankorCash:        'Cash',
            VendorType:        vendorType,
            CCCode:            selfCC,
            PaidToCC:          selfCC,
            PaidAganstCC:      txnMode === 'Debit from Other CC' ? otherCC : '',
            OtherCCCode:       txnMode === 'Debit from Other CC' ? otherCC : '',
            CashTransMode:     txnMode === 'Debit from Other CC' ? 2 : 1,
            Action:            'New', // Retention/Hold SPs act only on 'New' / 'Update'
        };

        // Add invoice-specific fields for non-advance types
        if (!isAdvance && selectedInvoiceData.length > 0) {
            payload.InvoiceNos     = selectedInvoiceData.map((inv) => inv.InvoiceNo).join(',') + ',';
            payload.MaxInvoiceDate = allInvoiceDates;
        }

        dispatch(saveVendorCashPayment({ paymentType, payload }));
    };

    const isBusy = saveLoading;

    // Display labels for the summary panel
    const selfCCLabel  = selfCCList.find((cc) => cc.CC_Code.split(',')[0].trim() === selfCC)?.CC_Code || selfCC;
    const otherCCLabel = otherCCList.find((cc) => cc.CC_Code.split(',')[0].trim() === otherCC)?.CC_Code || otherCC;
    const vendorLabel  = vendors.find((v) => v.VendorCode === vendorCode)?.VendorName || vendorCode;
    const paymentTypeLabel = paymentTypes.find((pt) => (pt.PaymentType || pt.Type) === paymentType)?.TypeDesc || paymentType;
    const paymentDateLabel = paymentDate
        ? new Date(paymentDate).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })
        : '';

    // ── Render ────────────────────────────────────────────────────────────────
    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-indigo-50/30 to-purple-50/30 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950 p-3 md:p-4">

            {/* ── Page Header (slim) ───────────────────────────────────────── */}
            <div className="max-w-7xl mx-auto mb-4">
                <div className="rounded-xl bg-gradient-to-r from-indigo-600 via-purple-600 to-violet-700 shadow-md shadow-indigo-500/20 px-5 py-3 text-white flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg bg-white/15 flex items-center justify-center border border-white/20">
                        <Receipt className="h-5 w-5 text-white" />
                    </div>
                    <div className="min-w-0">
                        <h1 className="text-lg font-black tracking-tight leading-tight truncate">
                            {menuData?.name || 'Vendor Cash Payment'}
                        </h1>
                        <p className="text-indigo-200 text-xs">Purchase Module · SPPO Vendor Payment by Cash</p>
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-4 items-start">

                {/* ── Left: single form card ───────────────────────────────── */}
                <div className="lg:col-span-2 bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
                    <CardHeader icon={Receipt} title="Payment Details" />
                    <div className="p-4 space-y-4">

                        {/* Vendor Type */}
                        <div className="flex flex-wrap items-center justify-between gap-3">
                            <span className="text-[11px] font-bold text-gray-600 dark:text-gray-400 uppercase tracking-wide">
                                Vendor Type<span className="text-rose-500 ml-0.5">*</span>
                            </span>
                            <div className="inline-flex rounded-lg border border-gray-300 dark:border-gray-600 overflow-hidden">
                                {['Supplier', 'Service Provider'].map((v) => (
                                    <button
                                        key={v}
                                        type="button"
                                        onClick={() => handleVendorTypeChange(v)}
                                        disabled={isBusy}
                                        className={`px-4 py-1.5 text-xs font-semibold transition-all disabled:opacity-50 ${vendorType === v
                                            ? 'bg-indigo-600 text-white'
                                            : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'}`}
                                    >
                                        {v}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {!vendorType ? (
                            <p className="text-sm text-gray-400 dark:text-gray-500 text-center py-6 border-t border-gray-100 dark:border-gray-700">
                                Select a vendor type to start the payment.
                            </p>
                        ) : (
                            <>
                                {/* Payment type, cost centres, vendor and date */}
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-3 pt-4 border-t border-gray-100 dark:border-gray-700">
                                    <Field label="Payment Type" required>
                                        <Select
                                            value={paymentType}
                                            onChange={handlePaymentTypeChange}
                                            disabled={isBusy}
                                            loading={paymentTypesLoading}
                                            options={paymentTypes}
                                            getValue={(pt) => pt.PaymentType || pt.Type}
                                            getLabel={(pt) => pt.TypeDesc || pt.PaymentType || pt.Type}
                                        />
                                    </Field>
                                    <Field label="Mode of Transaction" required>
                                        <Select
                                            value={txnMode}
                                            onChange={handleTxnModeChange}
                                            disabled={isBusy || !paymentType}
                                            placeholder={paymentType ? '-Please Select-' : 'Select Payment Type first'}
                                            options={[MODE_SELF, MODE_OTHER]}
                                            getValue={(o) => o}
                                            getLabel={(o) => o}
                                        />
                                    </Field>
                                    <Field label="Self Cost Center" required>
                                        <Select
                                            value={selfCC}
                                            onChange={handleSelfCCChange}
                                            disabled={isBusy || !txnMode}
                                            loading={selfCCLoading}
                                            placeholder={txnMode ? '-Please Select-' : 'Select Mode first'}
                                            options={selfCCList}
                                            getValue={(cc) => cc.CC_Code.split(',')[0].trim()}
                                            getLabel={(cc) => cc.CC_Code}
                                        />
                                    </Field>
                                    {txnMode === MODE_OTHER && (
                                        <Field label="Other Cost Center" required>
                                            <Select
                                                value={otherCC}
                                                onChange={handleOtherCCChange}
                                                disabled={isBusy || !selfCC}
                                                loading={otherCCLoading}
                                                placeholder={selfCC ? '-Please Select-' : 'Select Self CC first'}
                                                options={otherCCList}
                                                getValue={(cc) => cc.CC_Code.split(',')[0].trim()}
                                                getLabel={(cc) => cc.CC_Code}
                                            />
                                        </Field>
                                    )}
                                    <Field label="Vendor" required>
                                        <Select
                                            value={vendorCode}
                                            onChange={handleVendorChange}
                                            disabled={isBusy || !effectiveCCCode || !paymentType}
                                            loading={vendorsLoading}
                                            placeholder={!effectiveCCCode
                                                ? 'Select Cost Center first'
                                                : vendors.length === 0 ? 'No vendors available' : '-Please Select-'}
                                            options={vendors}
                                            getValue={(v) => v.VendorCode}
                                            getLabel={(v) => v.VendorName || v.VendorCode}
                                        />
                                    </Field>
                                    <Field label="Payment Date" required>
                                        <CustomDatePicker
                                            value={paymentDate}
                                            onChange={setPaymentDate}
                                            size="sm"
                                            format="DD/MM/YYYY"
                                            placeholder="Select payment date"
                                            disabled={isBusy}
                                        />
                                    </Field>
                                </div>
                                {vendorsError && <ErrorLine>{vendorsError}</ErrorLine>}

                                {/* Purchase Orders */}
                                {vendorCode && (
                                    <div className="pt-4 border-t border-gray-100 dark:border-gray-700">
                                        <SubHeading icon={ShoppingCart}>
                                            Purchase Orders{isAdvance ? ' (select one)' : ''}
                                        </SubHeading>
                                        {poError && <ErrorLine>{poError}</ErrorLine>}
                                        {poLoading ? (
                                            <div className="flex items-center gap-2 py-2 text-xs text-indigo-500">
                                                <Loader2 className="h-4 w-4 animate-spin" /> Loading POs...
                                            </div>
                                        ) : poList.length === 0 ? (
                                            <p className="text-xs text-gray-400 dark:text-gray-500 py-1">No POs found for this vendor.</p>
                                        ) : (
                                            <div className="flex flex-wrap gap-2">
                                                {poList.map((po, idx) => {
                                                    const poNo = po.SPPONo || po.PONo || po.PONumber || String(idx);
                                                    const isChecked = selectedPOs.includes(poNo);
                                                    return (
                                                        <label
                                                            key={poNo}
                                                            className={`flex items-center gap-1.5 h-8 px-3 rounded-lg border text-xs font-semibold cursor-pointer transition-colors
                                                                ${isChecked
                                                                    ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20 text-indigo-700 dark:text-indigo-300'
                                                                    : 'border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:border-indigo-300 bg-white dark:bg-gray-800'}`}
                                                        >
                                                            <input
                                                                type="checkbox"
                                                                checked={isChecked}
                                                                onChange={() => handlePOToggle(poNo)}
                                                                disabled={isBusy}
                                                                className="accent-indigo-500 w-3.5 h-3.5"
                                                            />
                                                            {poNo}
                                                        </label>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Invoices (non-advance) */}
                                {!isAdvance && selectedPOs.length > 0 && (
                                    <div className="pt-4 border-t border-gray-100 dark:border-gray-700">
                                        <SubHeading icon={ListChecks}>Invoices</SubHeading>
                                        {invoiceError && <ErrorLine>{invoiceError}</ErrorLine>}
                                        {invoiceLoading ? (
                                            <div className="flex items-center gap-2 py-2 text-xs text-indigo-500">
                                                <Loader2 className="h-4 w-4 animate-spin" /> Loading invoices...
                                            </div>
                                        ) : invoiceList.length === 0 ? (
                                            <p className="text-xs text-gray-400 dark:text-gray-500 py-1">No invoices found for selected POs.</p>
                                        ) : (
                                            <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
                                                <table className="w-full text-xs">
                                                    <thead>
                                                        <tr className="bg-gray-50 dark:bg-gray-900/40 text-[11px] font-bold text-gray-600 dark:text-gray-400 uppercase tracking-wide">
                                                            <th className="px-2.5 py-2 w-8" />
                                                            <th className="px-2.5 py-2 text-left">Invoice No</th>
                                                            <th className="px-2.5 py-2 text-left">PO No</th>
                                                            <th className="px-2.5 py-2 text-left">CC</th>
                                                            <th className="px-2.5 py-2 text-left">Invoice Date</th>
                                                            <th className="px-2.5 py-2 text-right">Net Amount</th>
                                                            <th className="px-2.5 py-2 text-right">Balance</th>
                                                            <th className="px-2.5 py-2 text-right w-32">Pay Amount</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                                                        {invoiceList.map((inv, idx) => {
                                                            const invNo = inv.InvoiceNo;
                                                            const sel   = invoiceSelections[invNo];
                                                            const isSelected = sel?.selected || false;
                                                            const payAmt     = sel?.payAmount ?? String(inv.Balance || '');
                                                            return (
                                                                <tr
                                                                    key={invNo || idx}
                                                                    className={isSelected ? 'bg-indigo-50/60 dark:bg-indigo-900/10' : 'hover:bg-gray-50 dark:hover:bg-gray-700/40'}
                                                                >
                                                                    <td className="px-2.5 py-1.5 text-center">
                                                                        <input
                                                                            type="checkbox"
                                                                            checked={isSelected}
                                                                            onChange={() => handleInvoiceToggle(invNo, inv.Balance)}
                                                                            disabled={isBusy}
                                                                            className="accent-indigo-500 w-3.5 h-3.5"
                                                                        />
                                                                    </td>
                                                                    <td className="px-2.5 py-1.5 font-semibold text-gray-800 dark:text-gray-200">{invNo}</td>
                                                                    <td className="px-2.5 py-1.5 text-gray-600 dark:text-gray-400">{inv.SPPONo || inv.PONo || inv.PONumber || '—'}</td>
                                                                    <td className="px-2.5 py-1.5 text-gray-600 dark:text-gray-400">{inv.CCCode || inv.CC || '—'}</td>
                                                                    <td className="px-2.5 py-1.5 text-gray-600 dark:text-gray-400">{inv.SPPOInvoiceDate || inv.InvoiceDate || '—'}</td>
                                                                    <td className="px-2.5 py-1.5 text-right text-gray-800 dark:text-gray-200">₹{fmt(inv.NetAmount)}</td>
                                                                    <td className="px-2.5 py-1.5 text-right font-semibold text-indigo-600 dark:text-indigo-400">₹{fmt(inv.Balance)}</td>
                                                                    <td className="px-2.5 py-1.5">
                                                                        <input
                                                                            type="text"
                                                                            inputMode="decimal"
                                                                            value={payAmt}
                                                                            onChange={(e) => { if (isAmountInput(e.target.value)) handlePayAmountChange(invNo, e.target.value, inv.Balance); }}
                                                                            disabled={!isSelected || isBusy}
                                                                            className={`${inputCls} h-8 text-right`}
                                                                        />
                                                                    </td>
                                                                </tr>
                                                            );
                                                        })}
                                                    </tbody>
                                                </table>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Remarks + amount */}
                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-4 border-t border-gray-100 dark:border-gray-700">
                                    <Field label="Remarks" className="sm:col-span-2">
                                        <input type="text" value={remarks} onChange={(e) => setRemarks(e.target.value)} disabled={isBusy} className={inputCls} />
                                    </Field>
                                    {isAdvance ? (
                                        <Field label="Advance Amount" required>
                                            <input
                                                type="text"
                                                inputMode="decimal"
                                                placeholder="0.00"
                                                value={advanceAmount}
                                                onChange={(e) => { if (isAmountInput(e.target.value)) setAdvanceAmount(e.target.value); }}
                                                disabled={isBusy || selectedPOs.length === 0}
                                                className={`${inputCls} text-right`}
                                            />
                                        </Field>
                                    ) : (
                                        <Field label="Invoice Total">
                                            <input
                                                type="text"
                                                readOnly
                                                value={fmt(totalAmount)}
                                                className={`${inputCls} text-right bg-gray-100 dark:bg-gray-700/60 font-semibold`}
                                            />
                                        </Field>
                                    )}
                                </div>
                            </>
                        )}
                    </div>
                </div>

                {/* ── Right: sticky summary + actions ──────────────────────── */}
                <div className="lg:sticky lg:top-4">
                    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
                        <CardHeader icon={CheckCircle2} title="Payment Summary" />
                        <div className="p-4 space-y-3">
                            <dl className="space-y-1.5 text-xs">
                                <SumRow label="Vendor Type"  value={vendorType} />
                                <SumRow label="Payment Type" value={paymentTypeLabel} />
                                <SumRow label="Mode"         value={txnMode} />
                                <SumRow label="Self CC"      value={selfCCLabel} />
                                {txnMode === MODE_OTHER && <SumRow label="Other CC" value={otherCCLabel} />}
                                <SumRow label="Vendor"       value={vendorLabel} />
                                <SumRow label="Payment Date" value={paymentDateLabel} />
                            </dl>

                            <dl className="space-y-1.5 text-xs pt-3 border-t border-gray-100 dark:border-gray-700">
                                <SumRow label="POs Selected" value={selectedPOs.length ? `${selectedPOs.length} PO(s)` : ''} />
                                {!isAdvance && (
                                    <SumRow label="Invoices Selected" value={selectedInvoiceData.length ? `${selectedInvoiceData.length} invoice(s)` : ''} />
                                )}
                            </dl>

                            <div className="px-4 py-3 rounded-lg text-white bg-gradient-to-r from-indigo-500 to-violet-600">
                                <p className="text-white/80 text-[10px] font-semibold uppercase tracking-wider">
                                    {isAdvance ? 'Advance Amount' : 'Total Amount'}
                                </p>
                                <p className="text-2xl font-black">₹{fmt(totalAmount)}</p>
                            </div>

                            <div className="flex gap-2">
                                <button
                                    type="button"
                                    onClick={handleReset}
                                    disabled={isBusy}
                                    className="flex items-center justify-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors border border-gray-200 dark:border-gray-600 disabled:opacity-50"
                                >
                                    <RotateCcw className="h-4 w-4" /> Reset
                                </button>
                                <button
                                    type="button"
                                    onClick={handleSubmit}
                                    disabled={isBusy || !vendorType}
                                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg font-semibold text-sm bg-gradient-to-r from-indigo-500 to-purple-600 text-white shadow-md shadow-indigo-500/30 hover:from-indigo-600 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                                >
                                    {isBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                                    {isBusy ? 'Saving...' : 'Submit Payment'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default VendorCashPayment;
